import React, {useContext, useEffect, useState} from 'react';

import {makeStyles} from "@material-ui/core/styles";

import Button from "@material-ui/core/Button";
import {useHistory} from "react-router-dom";
import {PermissionsContext, ErrorContext} from "../utils/constants";
import Colors from "../utils/colors";
import {getData} from "../utils/apiUtils";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import apiFetch from "../utils/apiUtils";
import { WorkstreamChip, SubWorkstreamChip }  from '../components/WorkstreamChips';
import Grid from '@material-ui/core/Grid';


const useStyles = makeStyles(() => ({
  h1: {
    fontWeight: 500,
    fontSize: '56px',
    lineHeight: '66px',
  },
  errorMsg: {
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '36px',
    color: 'red'
  },
  addContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%'
  },
  addArea: {
    width: '45%',
    backgroundColor: Colors.lightBlue,
    padding: '1rem'
  },
  inputStyle: {
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: 'white'
  },
  tableStyle: {
    border: '1px solid black',
    padding: '1rem'
  },
  table: {
    border: '1px solid black',
    width: '100%',
    marginTop: '3rem'
  }
}));

/* Note:  There's a huge disconnect between the UI and the data model.  
  In the UI, the Workstream is the workstream_group of the model.  The Sub Workstream
  is the workstream of the model.

  There a property called fuel in the workstream model which used to be the way with which
  the sub workstreams are classified in the UI.  This is deprecated in this page and should no longer be
  relevant.

  To make this more confusing, the workstream/sub workstream naming is only for admins.  The end users
  only ever see workstream.  So as a guide:

  Admin UI - Sub workstream, End user UI - Workstream, DB Table - workstreams
  Admin UI - Workstream, End user UI - No equivalent, DB Table - workstream_groups
*/

export default function AdminWorkstreamDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const { isAdmin } = useContext(PermissionsContext);
  const { triggerError } = useContext(ErrorContext);

  if (!isAdmin) {
    history.push('/ui/errors/error404');
  }
  const [ currentSubWorkstreamName, setCurrentSubWorkstreamName ] = useState('');
  const [ currentGroup, setCurrentGroup ] = useState({value: {id: ''}});
  const [ workstreams, setWorkstreams ] = useState([]);
  const [ approvers, setApprovers ] = useState([]);
  const [ subWorkStreams, setSubWorkStreams ] = useState([]);
  const [ workstreamsWithInitiatives, setWorkstreamsWithInitiatives] = useState([]);
  const [ errorMsg, setErrorMsg ] = useState('');
  const [ currentWorkstreamName, setCurrentWorkstreamName] = useState('');
 

  useEffect(() => {
    const onError = () => triggerError({
      title: 'Communication Error',
      description: 'We were unable to read your workstreams data from the server. Please try again.',
      onRetry: () => window.location.reload(),
    });
    
    getData('/users/getApprovers', setApprovers, onError);
    getData('/generaldata/workstreams', setSubWorkStreams, onError);
    getData('/generaldata/workstreams-groups', setWorkstreams, onError);
    getData('/generaldata/workstreams-withinitiatives', setWorkstreamsWithInitiatives, onError);

    document.title = 'Rise by TransAlta Admin Workstreams Dashboard';
  }, [triggerError]);

  const handleSubWorkstreamNameChange = (event) => {
    const value = event.target.value;
    setCurrentSubWorkstreamName(value);
  }
  
  const handleWorkstreamNameChange = (event) => {
    setCurrentWorkstreamName(event.target.value);
  };

  const doesSubWorkstreamExist = (name) => {
    return subWorkStreams.some(sws => sws.name === name);
  };

  const doesWorkstreamExist = (name) => {
    return workstreams.some(wsg => wsg.name === name);
  }

  const refreshWorkstreams = () => {
    getData('/generaldata/workstreams-groups', setWorkstreams, () => triggerError({
      title: 'Communication Error',
      description: 'We were unable to read your workstream data from the server. Please try again.',
      onRetry: () => window.location.reload(),
    }));
  };

  const refreshSubworkstreams = () => {
    getData('/generaldata/workstreams', setSubWorkStreams, () => triggerError({
      title: 'Communication Error',
      description: 'We were unable to read your sub workstreams data from the server. Please try again.',
      onRetry: () => window.location.reload(),
    }));
  };

  const handleWorkstreamAdd = () => {
    if (!currentWorkstreamName){
      setErrorMsg('You must input values for all fields!');
      return;
    }

    if(doesWorkstreamExist(currentWorkstreamName)) {
      setErrorMsg('Workstream already exists.');
      return;
    }

    const data = {name: currentWorkstreamName};
    apiFetch('/generaldata/workstreams-groups', 'POST',
      JSON.stringify(data),
      () => {
        setErrorMsg('');
        setCurrentWorkstreamName('');
        refreshWorkstreams();
      }, () => triggerError({
        title: 'Communication Error',
        description: `We were unable to add workstream ${currentWorkstreamName}. Please try again.`,
        onRetry: () => handleWorkstreamAdd(),
      }));
  };

  const handleWorksGroupChange = (event) => {
    const value = event.target.value;
    const group = workstreams.find(item => item.id === value);
    setCurrentGroup(group);
  };

  const handleSubWorkstreamAdd = () => {
    if (!currentGroup.id || !currentSubWorkstreamName){
      setErrorMsg('You must input values for all fields!');
      return;
    }
    
    if(doesSubWorkstreamExist(currentSubWorkstreamName)) {
      setErrorMsg('Sub Workstream already exists.');
      return;
    }
    const data = Object.assign({}, currentGroup);
    data.name = currentSubWorkstreamName;
    data.fuel = '';
    apiFetch('/generaldata/workstreams', 'POST',
      JSON.stringify(data),
      () => {
        setErrorMsg('');
        setCurrentSubWorkstreamName('');
        setCurrentGroup({});
        refreshSubworkstreams();
      }, () => triggerError({
        title: 'Communication Error',
        description: `We were unable to save workstream ${currentSubWorkstreamName} with fuel ${currentGroup.name}. Please try again.`,
        onRetry: () => handleSubWorkstreamAdd(),
      }));
  };

  const handleDelete = (id) => () => {
    // TODO: check if an approver
    const wsApprovers = approvers.filter(approver => approver.workstream_id === id);
    if (wsApprovers.length !== 0) {
      setErrorMsg('Please remove all approvers for the workstream before deleting!');
      return;
    }
    apiFetch('/generaldata/workstreams/' + id, 'DELETE',
      null,
      () => {
        setErrorMsg('');
        refreshSubworkstreams();
      }, () => triggerError({
        title: 'Unable to delete',
        description: 'You must modify impacted initiatives in order to delete.'
      })
    );
  };

  const handleWorkstreamDelete = (id) => () => {
    apiFetch('/generaldata/workstreams-groups/' + id, 'DELETE',
      null,
      () => {
        setErrorMsg('');
        refreshWorkstreams();
      }, () => triggerError({
        title: 'Unable to delete',
        description: 'You must modify impacted initiatives in order to delete.'
      })
    );
  };

  const handleWorkstreamRename = (workstream) => (newText, cb) => {
    if(doesWorkstreamExist(newText)) {
      triggerError({
        title: 'Update Error',
        description: `Workstream ${newText} already exists.`
      });
      cb(workstream.name);
      return;
    }

    apiFetch(`/generaldata/workstreams-groups/${workstream.id}/rename`, 'PATCH',
      JSON.stringify({ id: workstream.id, name: newText }),
      () => {
        refreshWorkstreams();
        cb(newText);
      },
      () => triggerError({
        title: 'Update Error',
        description: `We were unable to update the sub workstream. Please try again.`
      }));
  };

  const handleSubWorkstreamRename = (workstream) => (newText, cb) => {
    if(doesSubWorkstreamExist(newText)) {
      triggerError({
        title: 'Update Error',
        description: `Sub workstream ${newText} already exists.`
      });
      cb(workstream.name);
      return;
    }

    apiFetch(`/generaldata/workstreams/${workstream.id}/rename`, 'PATCH',
      JSON.stringify({ id: workstream.id, name: newText }),
      () => {
        cb(newText);
        refreshSubworkstreams();        
      },
      () => triggerError({
        title: 'Update Error',
        description: `We were unable to update the sub workstream. Please try again.`
      }));
  };

  const handleEnableDisable = (workstream) => (newState, cb) => {
    apiFetch(`/generaldata/workstreams/${workstream.id}/enableDisable`, 'PATCH',
      JSON.stringify({ id: workstream.id, is_disabled: newState }),
      () => {
        cb(newState);   
        refreshSubworkstreams();     
      },
      () => triggerError({
        title: 'Update Error',
        description: `We were unable to update the sub workstream. Please try again.`
      }));
  };

  const getSubWorkstreamChip = (subWorkStream) => {
    const hasInitiatives = !workstreamsWithInitiatives.some(wi => wi.workstream_id === subWorkStream.id);
    const canDelete = !approvers.some(a => a.group_id === subWorkStream.group_id || a.workstream_id === subWorkStream.id);
    return  <SubWorkstreamChip 
                  key={`${subWorkStream.id}-tag`} 
                  workstream={subWorkStream}
                  canDelete={canDelete && hasInitiatives}
                  onEnableDisable={handleEnableDisable(subWorkStream)}
                  onEdit={handleSubWorkstreamRename(subWorkStream)}
                  onDelete={handleDelete(subWorkStream.id)}
    />;
  };

  const getWorkstreamChip = (workstream) => {
    const canDelete = !subWorkStreams.some(sws => sws.group_id === workstream.id);
    return (<WorkstreamChip workstream={workstream}
      canDelete={canDelete}
      key={`${workstream.id}-wsg-tag`} 
      onEdit={handleWorkstreamRename(workstream)}
      onDelete={handleWorkstreamDelete(workstream.id)} />);
  };

  const getSubWorkStreamRow = (ws) => {
    const subWorkStreamsInGroup = subWorkStreams.filter(sws => ws.id === sws.group_id);
    const groupId = ws.id;

    return (
      <tr key={`row-${groupId}`}>
        <td className={classes.tableStyle} key={`sub-workstream-row-${groupId}`} id={`sub-workstream-row-${groupId}`}>
          {getWorkstreamChip(ws)}
          </td>
          <td className={classes.tableStyle} key={`table-cell-$${groupId}-members`} id={`table-cell-${groupId}-members`}>
              <Grid container spacing={3} direction="row">
                { subWorkStreamsInGroup.map(sws => getSubWorkstreamChip(sws)) }
              </Grid>
          </td>
      </tr>
    );
  };

  return (
    <main>
      <h1 className={classes.h1}>Workstreams</h1>
      { errorMsg ?
        <h1 className={classes.errorMsg}> {errorMsg}</h1> : null
      }
      <div className={classes.addContainer} >
      <div className={classes.addArea}>
          <h2>Add Workstream</h2>
          <TextField
            id={'text-box-workstream-name'}
            className={classes.inputStyle}
            style={{ width: '100%' }}
            variant="outlined"
            size="small"
            label={'Name'}
            onChange={handleWorkstreamNameChange}
            value={currentWorkstreamName}
          >
          </TextField>
          <div className={classes.addContainer} >
            <Button
              className={classes.btnStyle}
              variant="contained"
              onClick={handleWorkstreamAdd}
            >
              ADD
            </Button>
          </div>
        </div>
        <div className={classes.addArea}>
          <h2>Add Sub Workstream</h2>
          <TextField
            id={'text-box-sub-workstream-name'}
            className={classes.inputStyle}
            style={{ width: '100%' }}
            variant="outlined"
            size="small"
            label={'Name'}
            onChange={handleSubWorkstreamNameChange}
            value={currentSubWorkstreamName}
          >
          </TextField>
          <TextField
            id={'text-box-group'}
            className={classes.inputStyle}
            style={{ width: '100%' }}
            select
            variant="outlined"
            size="small"
            label={'Workstream'}
            onChange={handleWorksGroupChange}
            value={currentGroup.id || ''}
          >
            {workstreams.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>
          <div className={classes.addContainer} >
            <Button
              className={classes.btnStyle}
              variant="contained"
              onClick={handleSubWorkstreamAdd}
            >
              ADD
            </Button>
          </div>
        </div>
      </div>

      <div>
        <table className={classes.table}>
          <thead>
          <tr>
            <th className={classes.tableStyle} key="table-header-type">Workstream</th>
            <th className={classes.tableStyle} key="table-header-typstream">Sub Workstreams</th>
          </tr>
          </thead>
          <tbody>
              {workstreams.map(workStreamGroup => getSubWorkStreamRow(workStreamGroup) )}
          </tbody>
        </table>
      </div>

    </main>
  );
}
